import { Box, Grid, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
  getPlanFeatureBalanceUsage,
  getBulkPresignedURLForPut,
  fileUploadToS3,
  IFileInfoDTO,
  IBulkFileUploadDTO,
  getSubmissionSettings,
  ISubmissionSettingsResponse,
  parseUploadedCv,
  ICVBulkUploadDTO,
  getSkillsSuggestions,
  getJobById,
  IJob,
} from 'wisestep-api';
import {
  FileTypeVariants,
  FileUploadVariants,
  WsFileUpload,
  WsLocationSearch,
  WsAutoComplete,
  ControlledTextInput,
} from 'wisestep-base';

import { WsTagsFilter } from '../../../../components/filters/components';
import CandidateBulkSubmissions from '../../../../features/conversationWidget/components/BulkSubmission/CandidateBulkSubmissions';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import UploadCandidatesStatus from './UploadCandidatesStatus';
import UploadDeviceHeader from './UploadDeviceHeader';
import UploadPopup from './UploadPopup';

interface FileProgress {
  file: File;
  fileName: string;
  progress: number;
  presignedURL: string;
  error?: string;
}

export interface parsedCandidatesType {
  fileUrl: string;
  fileName: string;
  isParsed: boolean;
  candidate: any;
}
const UploadFromDeviceSubmission = () => {
  const methods = useForm<ICVBulkUploadDTO>();
  const { setValue, getValues } = methods;
  const [uploadLimitReached, setuploadLimitReached] = useState<boolean>();
  const [filesProgress, setFilesProgress] = useState<FileProgress[]>([]);
  const [skillList, setSkillList] = useState<string[]>([]);
  const [jobEntity, setJobEntity] = useState<IJob | null>(null);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [params] = useSearchParams();
  const jobId = String(params.get('jobId') ?? '');
  const maxFileSize = 3;

  const [submissionSetting, setSubmissionSetting] = useState<ISubmissionSettingsResponse | null>(
    null
  );
  const setSubmissionSettings = async () => {
    const setting = await getSubmissionSettings();
    setSubmissionSetting(setting.data);
  };

  const onFileUpload = async (file: File[]) => {
    const response = await getPlanFeatureBalanceUsage([
      'CS_UPLOAD_AND_PARSE_CV_COUNT',
      'CS_UPLOAD_AND_PARSE_CV_TOTAL_SIZE',
    ]);
    const fileAllowed = response['CS_UPLOAD_AND_PARSE_CV_COUNT'];
    const totalSizeAllowed = response['CS_UPLOAD_AND_PARSE_CV_TOTAL_SIZE'];

    if (fileAllowed <= 0) {
      setuploadLimitReached(true);
    } else if (totalSizeAllowed <= 0) {
      setuploadLimitReached(true);
    } else {
      const progressArray = file.map((file) => {
        return {
          file: file,
          fileName: file.name,
          progress: 0,
          presignedURL: '',
        };
      });
      setFilesProgress((fileProgress) => [...fileProgress, ...progressArray]);
    }
  };
  const onFileRejection = async (file: FileRejection[]) => {
    const progressArray = file.map((file) => {
      let errorMessage = file.errors[0].message;
      const code = file.errors[0].code;
      if (code === 'file-too-large') {
        errorMessage = `File is larger than ${maxFileSize} MB`;
      }
      return {
        file: file.file,
        fileName: file.file.name,
        error: errorMessage,
        progress: 0,
        presignedURL: '',
      };
    });
    setFilesProgress((fileProgress) => [...fileProgress, ...progressArray]);
  };

  const onSkillInputKey = async (skillInput: string) => {
    const result = await getSkillsSuggestions(skillInput);

    if (result.data) {
      setSkillList(result.data?.map((item) => item.skill));
    }
  };

  const onSkillSelection = (skillTags: string[]) => {
    setValue('skills', skillTags ?? []);
  };

  useEffect(() => {
    const getJob = async () => {
      if (jobId) {
        const job = await getJobById(jobId);
        setJobEntity(job.data);
        setValue('mapToJobId', job.data.title + '(' + job.data.jobCode + ')');
      }
    };
    getJob();
  }, [jobId, setValue]);

  const [parsedCandidates, setParsedCandidates] = useState<parsedCandidatesType[]>([]);

  const onUploadStart = async () => {
    setSubmissionSettings();
    const validFiles = filesProgress
      .filter((fileData) => !fileData.error)
      .map((fileData) => fileData.file);
    if (validFiles.length == 0) {
      setNotification(warning("Upload your CV's to start importing to Wisestep"));
      return;
    }
    const processRequest: ICVBulkUploadDTO = {
      totalSelected: validFiles.length,
      importFrom: 'LOCAL',
    };
    if (jobId) {
      processRequest.mapToJobId = jobId;
    }
    const response = await uploadFileToS3(validFiles);
    const fileProgressData = [...filesProgress];
    const skills = getValues('skills');
    const tags = getValues('tags');
    const locations = getValues('locations');
    const uploadedFiles: Partial<ICVBulkUploadDTO>[] = [];
    const parseCvs: Promise<AxiosResponse>[] = [];
    const fileUploads: Promise<AxiosResponse>[] = [];
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const fileUploadDetails = response.bulkFileList.filter((e) => e.fileName === file.name);
      let presignedUrl: IFileInfoDTO = {
        preSignedUrl: '',
        fileName: '',
      };
      if (fileUploadDetails?.length > 0) {
        presignedUrl = fileUploadDetails[0];
      }
      const pos = fileProgressData.map((e) => e.fileName).indexOf(file.name);
      fileProgressData[pos].presignedURL = presignedUrl.preSignedUrl ?? '';
      setFilesProgress([...fileProgressData]);
      if (presignedUrl.preSignedUrl) {
        try {
          fileUploads.push(
            fileUploadToS3(file, presignedUrl.preSignedUrl, (event: any) => {
              fileProgressData[pos].progress = (100 * event.loaded) / event.total;
              setFilesProgress([...fileProgressData]);
            })
          );
        } catch (e) {
          fileProgressData[pos].error = 'Failed To Upload';
        }
      }
      setFilesProgress([...fileProgressData]);
    }

    await Promise.all(fileUploads);
    fileProgressData
      .filter((file) => !file.error && file.presignedURL)
      .map((fileData) =>
        uploadedFiles.push({
          resumeURL: fileData.presignedURL.split('?')[0],
          resumeFileName: fileData.fileName,
          skills: skills,
          tags: tags,
          locations: locations,
        })
      );
    if (uploadedFiles?.length > 0) {
      try {
        const resp = parseCvHandler(uploadedFiles, setParsedCandidates);
        parseCvs.push(resp);
        setShowSubmissionForm(true);
      } catch (e) {
        setNotification(warning('Failed to parse resume'));
      }
    }
    setNotification(success('Resumes Uploaded'));
  };

  return (
    <Box bgcolor="white">
      <FormProvider {...methods}>
        <UploadDeviceHeader />
        <Grid container rowSpacing={2} columnSpacing={2} p={4}>
          <Grid item xs={6}>
            <WsTagsFilter placeHolder="Enter Tags, comma seprated" name="tags" label="Tags" />
          </Grid>
          <Grid item xs={6}>
            <WsAutoComplete
              onValueEnter={onSkillInputKey}
              onTagsChange={onSkillSelection}
              placeholder="Type to Select Skills"
              initialValue={[]}
              optionsInStringArray={skillList}
              multiple={true}
              freeSolo={true}
              name="skills"
              label="Skills"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <WsLocationSearch
              size="small"
              placeHolder="Select the Country of Candidates Profiles"
              name="locations"
              variant="outlined"
              showMandatorySymbol={true}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextInput
              name="mapToJobId"
              labelText="Job"
              fullWidth
              id="mapToJobId"
              variant="outlined"
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} pb={2}>
            <UploadCandidatesStatus
              selected={filesProgress.length}
              allowed={filesProgress.filter((file) => !file.error).length}
              notAllowed={filesProgress.filter((file) => file.error)?.length ?? 0}
              success={filesProgress.filter((file) => !file.error && file.progress == 100).length}
              failed={filesProgress.filter((file) => file.error).length}
            />
          </Grid>
          <Grid item xs={12}>
            <WsFileUpload
              multiple={true}
              name="uploadCandidates"
              placeholderHelperText="(.doc/.docx/.pdf/.odt/.html/.rtf/.txt files <= 3MB)"
              variant={FileUploadVariants.APP}
              onUpload={onFileUpload}
              onFileRejected={onFileRejection}
              placeholderText="Upload Candidates"
              fileTypes={FileTypeVariants.RESUME}
              maxFileSize={maxFileSize}
            />
          </Grid>
          <Grid item xs={12} pb={2} textAlign="center">
            <UploadPopup
              onStartUpload={onUploadStart}
              disabled={uploadLimitReached}
              open={filesProgress.length > 0}
            >
              {filesProgress.map((file, index) => (
                <>
                  <Grid
                    container
                    key={index}
                    px={2}
                    pt={2}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          wordWrap: 'break-word',
                        }}
                      >
                        {file.fileName}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    key={index}
                    pt={0.2}
                    px={2}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Grid item xs={11}>
                      <LinearProgress sx={{ p: 0 }} variant="determinate" value={file.progress} />
                    </Grid>
                    <Grid item xs={1} display="flex" justifyContent="center">
                      <Typography
                        sx={{
                          wordWrap: 'break-word',
                        }}
                      >{`${Math.round(file.progress)}%`}</Typography>
                    </Grid>
                    {file.error && (
                      <Grid item>
                        <Typography>{file.error}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              ))}
            </UploadPopup>
          </Grid>
        </Grid>
      </FormProvider>
      {showSubmissionForm && submissionSetting && (
        <CandidateBulkSubmissions
          parsedCandidatesData={parsedCandidates}
          job={jobEntity}
          close={() => {
            setShowSubmissionForm(false);
          }}
          openCandidateBulkSubmissions={showSubmissionForm}
          submissionSetting={submissionSetting}
          fromParser={true}
        />
      )}
    </Box>
  );
};

export default UploadFromDeviceSubmission;

const parseCvHandler = async (
  uploadedFiles: Partial<ICVBulkUploadDTO>[],
  setObject: (any) => void
) => {
  const payload: string[] = uploadedFiles.map((item) => item.resumeURL) as string[];
  const response = await parseUploadedCv({ urls: payload });
  const urlAndFileNameMap = new Map();

  uploadedFiles.forEach(function (item) {
    urlAndFileNameMap.set(item.resumeURL, item.resumeFileName);
  });
  const tempData = Object.entries(response.data.parsed_resume).map((item) => ({
    fileUrl: item[0],
    isParsed: true,
    fileName: urlAndFileNameMap.get(item[0]),
    candidate: item[1],
  }));
  setObject(tempData);
  return response;
};

export const uploadFileToS3 = async (files: File[]) => {
  const fileDetailsArray: IFileInfoDTO[] = [];
  files.map((file) => {
    const fileDetails: IFileInfoDTO = {
      fileName: file.name,
      fileType: file.type,
    };
    fileDetailsArray.push(fileDetails);
  });

  const request: IBulkFileUploadDTO = {
    bulkFileList: fileDetailsArray,
  };
  const response = await getBulkPresignedURLForPut(request);
  const responseData: IBulkFileUploadDTO = response.data;
  return responseData;
};
